import { fetchInboxMessages, warmMessageBodies } from "@/lib/api/imap";
import { markFolderBootstrapDone } from "@/lib/cache/folderBootstrapStore";
import { persistInboxHeadersSession } from "@/lib/cache/inboxHeadersSessionCache";
import {
  INBOX_BOOTSTRAP_FOLDER_GAP_MS,
  INBOX_BOOTSTRAP_SIZE,
  MAIL_LIVE_SYNC_INITIAL_DELAY_MS,
} from "@/lib/constants/inbox";
import { isDraftSaveInFlight } from "@/lib/compose/draftSaveLock";
import { getFolderSnapshot, publishFolderSnapshot } from "@/lib/mail/inboxFolderBus";
import { filterHeldMutationMessages } from "@/lib/mail/mailMutationHold";
import {
  idleDelay,
  isStandardBootstrapFolder,
  mergeBatchWithFolderSnapshot,
  shouldSkipFolderWarm,
  sortFoldersForBootstrap,
  syncFolderFromServerCache,
} from "@/lib/mail/folderWarmup";
import { isImapClientBusy } from "@/lib/mail/imapClientLock";
import type { MailFolder, MailMessageSummary } from "@/lib/types/mail";

export type FolderHeadersPrefetchResult = "skipped" | "cache" | "imap" | "empty" | "fail";

export type FolderHeadersPrefetchSummary = {
  done: number;
  skipped: number;
  failed: number;
  cacheHits: number;
  imapFetches: number;
};

async function waitForImapIdle(shouldAbort?: () => boolean): Promise<boolean> {
  while (isDraftSaveInFlight() || isImapClientBusy()) {
    if (shouldAbort?.()) return false;
    await idleDelay(400);
  }
  return !shouldAbort?.();
}

function applyPrefetchedHeaders(
  accountId: number,
  folder: string,
  batch: MailMessageSummary[],
  total: number,
  fromCache: boolean
): void {
  const messages = mergeBatchWithFolderSnapshot(accountId, folder, batch ?? []);
  const persistMessages = filterHeldMutationMessages(accountId, folder, messages);
  const removedCount = messages.length - persistMessages.length;
  const adjustedTotal = Math.max(0, total - removedCount);
  publishFolderSnapshot({
    accountId,
    folder,
    messages: persistMessages,
    total: adjustedTotal,
    offset: persistMessages.length,
    fromCache,
    bootstrapped: true,
  });
  persistInboxHeadersSession(accountId, folder, persistMessages, adjustedTotal, persistMessages.length);
  markFolderBootstrapDone(accountId, folder, persistMessages.length);
}

/**
 * Prépare les en-têtes d’un dossier (DD02 d’abord, IMAP seulement si miss).
 * N’appelle pas notifyNewMailArrived — pas de warm corps bulk (R2).
 */
export async function prefetchFolderHeaders(
  accountId: number,
  folder: string,
  options?: { force?: boolean; shouldAbort?: () => boolean }
): Promise<FolderHeadersPrefetchResult> {
  if (!options?.force && shouldSkipFolderWarm(accountId, folder)) {
    return "skipped";
  }

  if (!(await waitForImapIdle(options?.shouldAbort))) {
    return "fail";
  }

  const readRes = await fetchInboxMessages({
    accountId,
    folder,
    limit: INBOX_BOOTSTRAP_SIZE,
    offset: 0,
  });

  if (options?.shouldAbort?.()) return "fail";
  if (!readRes.ok) return "fail";

  const batch = readRes.data.messages ?? [];
  const total = readRes.data.total ?? batch.length;
  const cacheHit =
    readRes.data.cache === "hit" || readRes.data.cache === "partial-hit";

  if (batch.length > 0 || total === 0) {
    applyPrefetchedHeaders(accountId, folder, batch, total, cacheHit);
    return cacheHit ? "cache" : "imap";
  }

  if (total <= 0) {
    applyPrefetchedHeaders(accountId, folder, [], 0, true);
    return "empty";
  }

  if (!(await waitForImapIdle(options?.shouldAbort))) {
    return "fail";
  }

  const imapRes = await fetchInboxMessages({
    accountId,
    folder,
    limit: INBOX_BOOTSTRAP_SIZE,
    offset: 0,
    refresh: true,
  });

  if (options?.shouldAbort?.()) return "fail";
  if (!imapRes.ok) return "fail";

  const imapBatch = imapRes.data.messages ?? [];
  const imapTotal = imapRes.data.total ?? imapBatch.length;
  applyPrefetchedHeaders(accountId, folder, imapBatch, imapTotal, false);
  return imapBatch.length > 0 || imapTotal === 0 ? "imap" : "fail";
}

export async function prefetchAllFolderHeaders(
  accountId: number,
  folders: MailFolder[],
  options?: {
    force?: boolean;
    shouldAbort?: () => boolean;
    onProgress?: (done: number, total: number, folder: string, folderLabel: string) => void;
  }
): Promise<FolderHeadersPrefetchSummary> {
  const sorted = sortFoldersForBootstrap(folders);
  const targets = options?.force
    ? sorted
    : sorted.filter((f) => !shouldSkipFolderWarm(accountId, f.name));

  const summary: FolderHeadersPrefetchSummary = {
    done: 0,
    skipped: 0,
    failed: 0,
    cacheHits: 0,
    imapFetches: 0,
  };

  if (targets.length === 0) {
    summary.skipped = sorted.length;
    return summary;
  }

  let processed = 0;
  const total = targets.length;

  for (const entry of targets) {
    if (options?.shouldAbort?.()) break;

    options?.onProgress?.(processed, total, entry.name, entry.label);

    const result = await prefetchFolderHeaders(accountId, entry.name, {
      force: options?.force,
      shouldAbort: options?.shouldAbort,
    });

    processed += 1;
    options?.onProgress?.(processed, total, entry.name, entry.label);

    if (result === "skipped") summary.skipped += 1;
    else if (result === "fail") summary.failed += 1;
    else {
      summary.done += 1;
      if (result === "cache" || result === "empty") summary.cacheHits += 1;
      if (result === "imap") summary.imapFetches += 1;
    }

    if (options?.shouldAbort?.()) break;
    await idleDelay(INBOX_BOOTSTRAP_FOLDER_GAP_MS);
  }

  return summary;
}

export function countFoldersNeedingPrefetch(
  accountId: number,
  folders: MailFolder[]
): { ready: number; total: number } {
  const sorted = sortFoldersForBootstrap(folders);
  const total = sorted.length;
  const ready = sorted.filter((f) => shouldSkipFolderWarm(accountId, f.name)).length;
  return { ready, total };
}

const standardWarmQueue: Array<{ accountId: number; folder: string }> = [];
const standardWarmQueued = new Set<string>();
const standardBodiesAsked = new Set<string>();
let standardWarmDrain: Promise<void> | null = null;

function standardWarmKey(accountId: number, folder: string): string {
  return `${accountId}\0${folder}`;
}

/** Copie les 50 corps (DD02) pour que les deux coches puissent s’afficher. La réception a déjà son archive. */
async function warmStandardFolderBodies(accountId: number, folder: string): Promise<void> {
  const key = standardWarmKey(accountId, folder);
  if (folder.toUpperCase() === "INBOX") {
    standardBodiesAsked.add(key);
    return;
  }
  if (standardBodiesAsked.has(key)) return;

  let snap = getFolderSnapshot(accountId, folder);
  if (!snap?.messages.length) {
    await syncFolderFromServerCache(accountId, folder);
    snap = getFolderSnapshot(accountId, folder);
  }
  const uids = (snap?.messages ?? [])
    .map((message) => message.uid)
    .filter((uid) => uid > 0)
    .slice(0, INBOX_BOOTSTRAP_SIZE);
  if (uids.length === 0) return;

  standardBodiesAsked.add(key);
  await warmMessageBodies({ accountId, folder, uids });
}

/** Fenêtre 50 : en-têtes, puis copie des corps. Un dossier à la fois. */
export function enqueueStandardFolderWarm(accountId: number, folders: MailFolder[]): void {
  const sorted = sortFoldersForBootstrap(folders).filter((folder) =>
    isStandardBootstrapFolder(folder.name)
  );
  for (const folder of sorted) {
    const key = standardWarmKey(accountId, folder.name);
    if (standardWarmQueued.has(key)) continue;
    if (standardBodiesAsked.has(key) && shouldSkipFolderWarm(accountId, folder.name)) continue;
    standardWarmQueued.add(key);
    standardWarmQueue.push({ accountId, folder: folder.name });
  }
  if (standardWarmDrain == null) {
    standardWarmDrain = drainStandardFolderWarm();
  }
}

function delayMs(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function runStandardWarmJob(job: { accountId: number; folder: string }): Promise<void> {
  const key = standardWarmKey(job.accountId, job.folder);
  try {
    if (!shouldSkipFolderWarm(job.accountId, job.folder)) {
      await prefetchFolderHeaders(job.accountId, job.folder);
    }
    await warmStandardFolderBodies(job.accountId, job.folder);
  } finally {
    standardWarmQueued.delete(key);
  }
  await idleDelay(INBOX_BOOTSTRAP_FOLDER_GAP_MS);
}

async function drainStandardFolderWarm(): Promise<void> {
  const othersReadyAt = Date.now() + MAIL_LIVE_SYNC_INITIAL_DELAY_MS;
  try {
    while (standardWarmQueue.length > 0) {
      const inboxIndex = standardWarmQueue.findIndex(
        (job) => job.folder.toUpperCase() === "INBOX"
      );
      if (inboxIndex < 0 && othersReadyAt > Date.now()) {
        await delayMs(Math.min(othersReadyAt - Date.now(), 400));
        continue;
      }
      const job =
        inboxIndex >= 0
          ? standardWarmQueue.splice(inboxIndex, 1)[0]
          : standardWarmQueue.shift();
      if (!job) break;
      await runStandardWarmJob(job);
    }
  } finally {
    standardWarmDrain = null;
    if (standardWarmQueue.length > 0) {
      standardWarmDrain = drainStandardFolderWarm();
    }
  }
}
